import { ErrorMessage, Field, Form, Formik } from 'formik';
import { AlertCircle, ChevronDown, ChevronUp, Info, Loader2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import * as Yup from 'yup';
import { postOperationInstruction, updateOperationInstructionByID } from '../../../services/operationInstructionServices';
import { getSites } from '../../../services/superAdminServices';
import StandardDatePicker from '../../../components/DatePicker/DatePicker';

interface ApparatusItem {
  name: string;
  instruction: string;
}

interface AddOperatingInstructionProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (returnedItem: any, type: 'create' | 'update') => void;
  onError: (errorMessage: string) => void;
  editData?: any;
}

const AddOperatingInstruction: React.FC<AddOperatingInstructionProps> = ({ isOpen, onClose, onConfirm, onError, editData }) => {
  const [apparatusList, setApparatusList] = useState<ApparatusItem[]>([]);
  const [apparatusName, setApparatusName] = useState('');
  const [apparatusDesc, setApparatusDesc] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [savingApparatus, setSavingApparatus] = useState(false);

  const [sites, setSites] = useState<{ id: number; name: string }[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);

  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);
  const [apparatusError, setApparatusError] = useState('');
  const dateRef = useRef<HTMLInputElement>(null);

  const titleRef = useRef<HTMLInputElement>(null);
  const siteRef = useRef<HTMLSelectElement>(null);
  const apparatusNameRef = useRef<HTMLInputElement>(null);
  const quillRef = useRef<ReactQuill>(null);

  const MAX_OPERATION_LENGTH = 500;

  const operationText = apparatusDesc.replace(/<[^>]+>/g, '').trim();

  const operationLength = operationText.length;

  useEffect(() => {
    if (editData) {
      // editData.apparatus might be undefined or array
      setApparatusList(Array.isArray(editData.apparatus) ? editData.apparatus : []);
    } else {
      setApparatusList([]);
    }
    setApparatusName('');
    setApparatusDesc('');
    setEditingIndex(null);
    setExpandedIndex(null);
    setApparatusError('');
  }, [editData]);

  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true);
      try {
        const res = await getSites();
        const payload = res?.data?.data || res?.data || res || [];
        setSites(Array.isArray(payload) ? payload : []);
      } catch (error: any) {
        console.error('Error fetching sites:', error);
        const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load sites';
        setApiError(message);
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
        setSites([]);
      } finally {
        setLoadingSites(false);
      }
    };
    fetchSites();
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const resetApparatusEditor = () => {
    setApparatusName('');
    setApparatusDesc('');
    setEditingIndex(null);
    setApparatusError('');
  };

  const handleSaveApparatus = () => {
    if (savingApparatus) return;

    setSavingApparatus(true);

    window.setTimeout(() => {
      try {
        const plainText = apparatusDesc.replace(/<[^>]+>/g, '').trim();

        if (!apparatusName.trim()) {
          setApparatusError('Apparatus name is required');
          scrollToField(apparatusNameRef.current);
          return;
        }

        if (!plainText) {
          setApparatusError('Operation to be carried out is required');

          const editor = quillRef.current?.getEditor();
          const editorElement = editor?.root;

          if (editorElement) {
            editorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });

            setTimeout(() => editor.focus(), 300);
          }

          return;
        }

        if (plainText.length > MAX_OPERATION_LENGTH) {
          setApparatusError('Operation cannot exceed 500 characters');

          const editor = quillRef.current?.getEditor();
          editor?.focus();

          return;
        }

        const updatedItem = {
          name: apparatusName.trim(),
          instruction: apparatusDesc,
        };

        setApparatusList((prev) => {
          if (editingIndex === null) return [...prev, updatedItem];

          return prev.map((item, index) => (index === editingIndex ? updatedItem : item));
        });

        resetApparatusEditor();
      } finally {
        setSavingApparatus(false);
      }
    }, 500);
  };

  const handleEditApparatus = (index: number) => {
    const item = apparatusList[index];
    setApparatusName(item.name);
    setApparatusDesc(item.instruction);
    setEditingIndex(index);
    setExpandedIndex(index);
    setApparatusError('');
    scrollToField(apparatusNameRef.current);
  };

  const handleRemoveApparatus = (index: number) => {
    setApparatusList((prev) => prev.filter((_, itemIndex) => itemIndex !== index));

    if (editingIndex === index) {
      resetApparatusEditor();
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }

    setExpandedIndex((currentIndex) => {
      if (currentIndex === index) return null;
      if (currentIndex !== null && currentIndex > index) return currentIndex - 1;
      return currentIndex;
    });
  };

  const toggleAccordion = (index: number) => {
    setExpandedIndex(index === expandedIndex ? null : index);
  };

  if (!isOpen) return null;

  // helper: ensure date string in YYYY-MM-DD
  const formatDateForInput = (input?: string) => {
    if (!input) return '';

    const dateOnlyMatch = input.match(/^(\d{4}-\d{2}-\d{2})/);
    if (dateOnlyMatch) return dateOnlyMatch[1];

    try {
      const d = new Date(input);
      if (isNaN(d.getTime())) return '';

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const getTodayDate = () => new Date().toISOString().slice(0, 10);

  const scrollToField = (element: HTMLElement | null) => {
    if (!element) return;

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    setTimeout(() => {
      element.focus();
    }, 300);
  };

  const selectedSiteId = sites.find((site) => site.name?.trim().toLowerCase() === editData?.site?.trim().toLowerCase())?.id || '';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-lg p-6 relative max-h-[90vh] overflow-y-auto">
        <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-800" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="text-lg font-semibold mb-4">{editData ? 'Edit Operation Instruction' : 'Add Operating Instruction'}</h3>

        <Formik
          enableReinitialize
          initialValues={{
            title: editData?.title || '',
            site: String(selectedSiteId),
            date: formatDateForInput(editData?.instruction_date) || getTodayDate(),
          }}
          validationSchema={Yup.object({
            title: Yup.string().required('Title is required'),
            site: Yup.string().required('Site is required'),
            date: Yup.string().required('Date is required'),
          })}
          onSubmit={async (values, { resetForm, setSubmitting }) => {
            if (editingIndex !== null) {
              setApparatusError('Update or cancel the apparatus edit before submitting');
              scrollToField(apparatusNameRef.current);
              return;
            }

            if (apparatusList.length === 0) {
              setApparatusError('At least one apparatus is required');
              return;
            }
            setSubmitting(true);
            try {
              const payload = {
                title: values.title,
                site_id: Number(values.site),
                instruction_date: values.date,
                apparatus: apparatusList,
              };

              if (editData?.id) {
                const res = await updateOperationInstructionByID(editData.id, payload);
                const returned = res?.data?.data || res?.data || res || payload;
                onConfirm(returned, 'update');
              } else {
                const res = await postOperationInstruction(payload);
                const returned = res?.data?.data || res?.data || res || payload;
                onConfirm(returned, 'create');
              }

              resetForm();
              setApparatusList([]);
              resetApparatusEditor();
              onClose();
            } catch (error: any) {
              console.error('Failed to save instruction:', error);

              // Extract error message from API response
              let errorMessage = 'Failed to save operation instruction.';

              if (error?.response?.data) {
                const errorData = error.response.data;

                // Check for validation errors object
                if (errorData.errors) {
                  const errorMessages = Object.values(errorData.errors).flat().join(' ');
                  errorMessage = errorMessages || errorData.message || errorMessage;
                } else if (errorData.message) {
                  errorMessage = errorData.message;
                } else if (errorData.error) {
                  errorMessage = errorData.error;
                }
              } else if (error?.message) {
                errorMessage = error.message;
              }

              setApiError(errorMessage);
              setShowError(true);
              setTimeout(() => setShowError(false), 5000);

              // Also call onError for parent component
              onError(errorMessage);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, values, validateForm, setTouched, setFieldValue }) => (
            <Form>
              {/* Title */}
              <div className="mb-4">
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <span className="text-red-500">*</span>
                  <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
                </div>
                <Field
                  innerRef={titleRef}
                  type="text"
                  name="title"
                  placeholder="Enter Title"
                  maxLength={100}
                  className="w-full border rounded-md px-3 py-2  outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100 "
                />
                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${values.title.length >= 100 ? 'text-red-500' : 'text-gray-500'}`}>
                    ( Maximum 100 characters allowed )
                  </p>

                  <p className="text-xs text-gray-500">{values.title.length}/100</p>
                </div>
                <ErrorMessage name="title" component="div" className="text-sm text-red-600 mt-1" />
              </div>

              {/* Site + Date */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="block text-sm font-medium mb-1">Site</label>
                    <span className="text-red-500">*</span>
                    <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
                  </div>
                  <Field name="site">
                    {({ field }: any) => (
                      <select
                        ref={siteRef}
                        {...field}
                        className={`w-full border rounded-md px-3 py-2 bg-gray-100 
                        ${!field.value ? 'text-gray-400' : 'text-gray-700'}
                        outline-none focus:outline-none focus:ring-0 focus:border-gray-300`}
                      >
                        <option value="" disabled hidden>
                          Select Site
                        </option>

                        {loadingSites ? (
                          <option disabled>Loading...</option>
                        ) : (
                          sites.map((site) => (
                            <option key={site.id} value={String(site.id)} className="text-gray-700">
                              {site.name}
                            </option>
                          ))
                        )}
                      </select>
                    )}
                  </Field>
                  <ErrorMessage name="site" component="div" className="text-sm text-red-600 mt-1" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Date
                    <span className="text-red-500">*</span>
                  </label>
                  <Field name="date">
                    {({ field }: any) => (
                      <StandardDatePicker
                        inputRef={dateRef}
                        value={field.value}
                        min={editData ? undefined : getTodayDate()}
                        onChange={(value) => setFieldValue('date', value)}
                      />
                    )}
                  </Field>
                  <ErrorMessage name="date" component="div" className="text-sm text-red-600 mt-1" />
                </div>
              </div>

              {/* Apparatus input */}
              <div className="mb-5 border border-gray-200 rounded-md p-4 bg-white">
                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-sm font-medium mb-1">Apparatus</label>
                  <span className="text-red-500">*</span>
                  <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
                </div>
                <input
                  ref={apparatusNameRef}
                  type="text"
                  value={apparatusName}
                  onChange={(e) => setApparatusName(e.target.value)}
                  maxLength={100}
                  placeholder="Enter Apparatus"
                  className="w-full border rounded-md px-3 py-2  outline-none focus:outline-none focus:ring-0 focus:border-gray-300 bg-gray-100 "
                />
                <div className="flex justify-between mt-1 mb-4">
                  <p className={`text-xs ${apparatusName.length >= 100 ? 'text-red-500' : 'text-gray-500'}`}>
                    ( Maximum 100 characters allowed )
                  </p>

                  <p className="text-xs text-gray-500">{apparatusName.length}/100</p>
                </div>

                <div className="flex items-center gap-1 mb-1">
                  <label className="block text-sm font-medium mb-1">
                    Operation to be Carried Out
                    <span className="text-red-500">*</span>
                  </label>
                  <Info className="w-4 h-4 text-gray-400 cursor-pointer" />
                </div>
                <ReactQuill
                  ref={quillRef}
                  value={apparatusDesc}
                  onChange={setApparatusDesc}
                  placeholder="Add description here"
                  className="bg-gray-100 custom-quill"
                  theme="snow"
                />

                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${operationLength >= MAX_OPERATION_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
                    ( Maximum 500 characters allowed )
                  </p>

                  <p className="text-xs text-gray-500">{operationLength}/500</p>
                </div>

                {operationLength > MAX_OPERATION_LENGTH && (
                  <div className="text-sm text-red-600 mt-1">Operation to be Carried Out cannot exceed 500 characters</div>
                )}

                <div className="flex items-center gap-2 mt-3">
                  <button
                    type="button"
                    onClick={handleSaveApparatus}
                    disabled={savingApparatus}
                    className="px-4 py-2 border border-black text-black rounded-md disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="flex items-center gap-2">
                      {savingApparatus && <Loader2 className="h-4 w-4 animate-spin" />}
                      {savingApparatus ? (editingIndex === null ? 'Adding...' : 'Updating...') : editingIndex === null ? 'Add' : 'Update'}
                    </span>
                  </button>

                  {editingIndex !== null && (
                    <button
                      type="button"
                      onClick={resetApparatusEditor}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md"
                    >
                      Cancel Edit
                    </button>
                  )}
                </div>

                {/* Apparatus list INSIDE the box */}
                {apparatusError && <div className="text-sm text-red-600 mt-3">{apparatusError}</div>}

                <div className="space-y-3 mt-4">
                  {apparatusList.length === 0 && <div className="text-sm text-gray-500">No apparatus added.</div>}

                  {apparatusList.map((item, index) => (
                    <div key={index} className="border rounded-md">
                      <div
                        className="flex justify-between items-center px-4 py-3 cursor-pointer bg-gray-50"
                        onClick={() => toggleAccordion(index)}
                      >
                        <div className="text-sm font-medium">
                          {index + 1}. {item.name}
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditApparatus(index);
                            }}
                            className="text-sm text-blue-600 border border-blue-600 px-4 py-2 rounded"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveApparatus(index);
                            }}
                            className="text-sm text-red-500 border border-red-500 px-4 py-2 rounded"
                          >
                            Remove
                          </button>

                          {expandedIndex === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </div>

                      {expandedIndex === index && (
                        <div className="p-4 bg-white border-t">
                          <h4 className="text-sm font-semibold text-gray-500 mb-2">Operation to be carried out</h4>

                          <div
                            className="text-sm text-gray-800 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{
                              __html: item.instruction,
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 pt-4 mt-4">
                <button type="button" className="px-4 py-2 border rounded-md hover:bg-gray-100" onClick={onClose}>
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={async () => {
                    const errors = await validateForm();

                    if (errors.title) {
                      scrollToField(titleRef.current);
                      return;
                    }

                    if (errors.site) {
                      scrollToField(siteRef.current);
                      return;
                    }

                    if (errors.date) {
                      scrollToField(dateRef.current);
                      return;
                    }

                    if (editingIndex !== null) {
                      setApparatusError('Update or cancel the apparatus edit before submitting');
                      scrollToField(apparatusNameRef.current);
                      return;
                    }

                    if (apparatusList.length === 0) {
                      setApparatusError('At least one apparatus is required');

                      scrollToField(apparatusNameRef.current);
                      return;
                    }

                    document.querySelector('form')?.requestSubmit();
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : editData ? 'Update' : 'Add'}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        {/* Error Notification */}
        {showError && (
          <div className="fixed bottom-6 right-6 z-[100]">
            <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
              <button
                onClick={() => setShowError(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold mb-1">Error</h4>
                  <p className="text-sm">{apiError}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddOperatingInstruction;
